import React, { useEffect, useState } from 'react';
import Breadcrumb from '../../DrugDetails/Breadcrumb';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import _ from 'lodash';
import { Helmet } from 'react-helmet';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import {
  CONDITIONS_DETAIL_CANONICAL,
  HOME_CANONICAL,
  MEDICALLY_REVIEWED_BY,
  WRITTEN_BY
} from '../../../constants/defaults';
import RelatedArticles from '../../common/RelatedArticles/index.jsx';
import PopularDrugs from '../../common/PopularDrugs';
import { fetchDrugClassRelatedBlogs } from '../../../services/Api/DrugInfo';
import {
  CONDITIONS_ADD_ON_PAGE_ADS,
  GPT_HEAD_CONDITIONS_ADD_ON_PAGE
} from '../../../constants/gptHeadTags/conditionsPage';
import AdSlot from '../../dfp/AdSlot';
import {
  AVERAGE_HORIZONTAL_AD_HEIGHT,
  AVERAGE_VERTICAL_AD_HEIGHT,
  BOTTOM_ANCHOR_AD_CONTAINER_ID,
  HORIZONTAL_ADS_CONTAINER_ID,
  MINIMUM_AD_SPACING,
  VERTICAL_ADS_CONTAINER_ID
} from '../../../constants/gptHeadTags/common';
import {
  injectHorizontalAds,
  insertVerticalAds
} from '../../dfp/DynamicAdsInjection';
import FactCheckModal from '../../common/FactCheckModal';
import AuthorProfile from '../../common/AuthorProfile/index.js';
import KeyTakeawaysSection from '../../common/KeyTakeawaysSection/index.js';
import PublishedDate from '../../common/PublishedDate/index.js';
import TableOfContents from '../../common/TableOfContnents/index.js';
import ConditionTabs from '../../common/ConditionTabs/index.js';

const { API } = require('../../../config/' + process.env.REACT_APP_ENV);

const ConditionAddOn = ({ data, location }) => {
  const [blogData, setBlogData] = useState([]);
  const slugType = data.slugType;
  const content = _.checkParseString(data?.mainContent);
  const introContent = _.checkParseString(data?.introContent);
  const keyTakeaways = _.checkParseString(data?.keyTakeaways);
  const faQs = _.checkParseString(data?.faQs);
  const references = _.checkParseString(data?.references);
  const summary = _.checkParseString(data?.summary);
  const RelatedDrugsInformationRoute = {
    WhatIsPages: '/what-is',
    DrugDosageGuide: '/dosage'
  };
  const relatedDrugInformation =
    data &&
    data.relatedDrugInformation.filter(
      infoItem => infoItem.type !== 'SideEffects'
    );

  const typeAndURL = {
    symptoms: { url: 'conditions-symptoms', title: 'Symptoms Directory' },
    causes: { url: 'conditions-causes', title: 'Causes Directory' },
    directory: { url: 'questions', title: 'FAQs' }
  };
  const routeUrlByType = type => {
    return typeAndURL[type]['url'];
  };
  const items = [
    ...(slugType == 'directory' ? [] : [{ to: '/', label: 'Home' }]),
    { to: `/${routeUrlByType(slugType)}`, label: typeAndURL[slugType]['title'] }
  ];
  const isAdsVisible = API.ALLOW_CONDITIONS_ADD_ON_PAGE_ADS;
  const aidName = data?.slug?.split(`-${slugType}`)?.[0];
  const typeAndAdsId = {
    symptoms: {
      top: 'div-gpt-ad-1694719068330-0',
      bottom: 'div-gpt-ad-1694719095144-0',
      bottomAnchor: 'div-gpt-ad-1694719594975-0'
    },
    causes: {
      top: 'div-gpt-ad-1694721278359-0',
      bottom: 'div-gpt-ad-1694721306141-0',
      bottomAnchor: 'div-gpt-ad-1694721309299-0'
    }
  };

  const handleBottomAdScroll = () => {
    const viewportHeight = window.innerHeight;
    const bottomTriggerElement = document.querySelector('.homepage-footer');
    const anchorDiv = document.querySelector(
      `[data-ads-container="${BOTTOM_ANCHOR_AD_CONTAINER_ID.CONDITIONS_ADD_ON_PAGE}"]`
    );
    // Check if the user has scrolled at least 1.5 times the height of the viewport
    if (window.scrollY >= 1.5 * viewportHeight) {
      // Check if the footer is visible
      if (bottomTriggerElement.getBoundingClientRect()?.top <= viewportHeight) {
        // Show the anchor div
        anchorDiv.classList.remove('bottomAdsBanner');
      } else {
        // Hide the anchor div
        anchorDiv.classList.add('bottomAdsBanner');
      }
    } else {
      // Hide the anchor div
      anchorDiv.classList.remove('bottomAdsBanner');
    }
  };

  useEffect(() => {
    fetchDrugClassRelatedBlogs(encodeURI(data?.name), 12).then(res => {
      if (res) setBlogData(res?.posts);
    });
    if (isAdsVisible) {
      // bottom anchor ads conditions
      const anchorDiv = document.querySelector(
        `[data-ads-container="${BOTTOM_ANCHOR_AD_CONTAINER_ID.CONDITIONS_ADD_ON_PAGE}"]`
      );
      const viewportHeight = window.innerHeight;
      // Check if the total page is is at least 3X if the viewport height
      if (document.body.scrollHeight >= 3 * viewportHeight) {
        // Show the anchor div
        window.addEventListener(
          'scroll',
          _.throttle(handleBottomAdScroll, 250)
        );
      } else {
        anchorDiv.classList.remove('bottomAdsBanner');
      }

      // dynamic ads injections
      const { sizeMapping } = window;
      const onSizeMappingLoad = () => {
        injectHorizontalAds(
          CONDITIONS_ADD_ON_PAGE_ADS(sizeMapping.inContent)?.[slugType]
            ?.IN_CONTENT,
          `[data-ads-containter="${HORIZONTAL_ADS_CONTAINER_ID.CONDITIONS_ADD_ON_PAGE_IN_CONTENT}"]`,
          '#app',
          AVERAGE_HORIZONTAL_AD_HEIGHT,
          isAdsVisible,
          MINIMUM_AD_SPACING
        );
        insertVerticalAds(
          CONDITIONS_ADD_ON_PAGE_ADS(sizeMapping.leftRight)?.[slugType]
            ?.LEFT_SIDE,
          `[data-ads-containter="${VERTICAL_ADS_CONTAINER_ID.CONDITION_ADD_ON_PAGE_LEFT_SIDE}"]`,
          '#app',
          AVERAGE_VERTICAL_AD_HEIGHT,
          isAdsVisible
        );
        insertVerticalAds(
          CONDITIONS_ADD_ON_PAGE_ADS(sizeMapping.leftRight)?.[slugType]
            ?.RIGHT_SIDE,
          `[data-ads-containter="${VERTICAL_ADS_CONTAINER_ID.CONDITION_ADD_ON_PAGE_RIGHT_SIDE}"]`,
          '#app',
          AVERAGE_VERTICAL_AD_HEIGHT,
          isAdsVisible
        );
      };
      if (typeof sizeMapping !== 'undefined') {
        if (sizeMapping.inContent) {
          onSizeMappingLoad();
        } else {
          const sizeMappingListener = () => {
            if (sizeMapping.inContent) {
              return onSizeMappingLoad();
            }
            return setTimeout(sizeMappingListener, 200);
          };
          sizeMappingListener();
        }
      }
    }
  }, []);

  const renderOptions = links => {
    const entryBlockMap = new Map();

    if (links && !_.isEmpty(links.entries)) {
      for (const entry of links.entries.block) {
        entryBlockMap.set(entry.sys.id, entry);
      }
    }
    const assetBlockMap = new Map();
    if (links && !_.isEmpty(links.assets)) {
      for (const asset of links.assets.block) {
        assetBlockMap.set(asset.sys.id, asset);
      }
    }
    return {
      renderNode: {
        [BLOCKS.EMBEDDED_ENTRY]: node => {
          const entry = entryBlockMap.get(node.data.target.sys.id);
          return (
            <>
              {entry && entry.tableData && (
                <span
                  dangerouslySetInnerHTML={{
                    __html: entry.tableData
                  }}
                ></span>
              )}
            </>
          );
        },
        [BLOCKS.EMBEDDED_ASSET]: node => {
          const asset = assetBlockMap.get(node.data.target.sys.id);
          return (
            <img
              className="contentImg"
              src={asset.url}
              alt={asset.description}
              title={asset.title}
            />
          );
        },
        [INLINES.HYPERLINK]: node => {
          if (node?.data?.uri.indexOf('youtube.com') !== -1) {
            return (
              <div className="videoHolder">
                <iframe
                  title="YouTube video player"
                  src={node?.data?.uri}
                  width="560"
                  height="315"
                  frameBorder="0"
                  allowfullscreen="allowfullscreen"
                ></iframe>
              </div>
            );
          } else return <a href={node?.data?.uri}>{node?.content[0]?.value}</a>;
        },
        [BLOCKS.HEADING_2]: node => {
          return (
            <h2 id={_.createIDFromHeader(node?.content[0]?.value)}>
              {node?.content[0]?.value}
            </h2>
          );
        },
        [BLOCKS.HEADING_3]: node => {
          return (
            <h3 id={_.createIDFromHeader(node?.content[0]?.value)}>
              {node?.content[0]?.value}
            </h3>
          );
        }
      }
    };
  };

  const getMetaData = () => {
    return (
      <Helmet>
        <title>{data?.metaTitle || data.h1Title}</title>
        <link
          rel="canonical"
          href={`${HOME_CANONICAL}/conditions/${data.slug}`}
        />
        <script type="application/ld+json">{getLdJson(data)}</script>
        <meta name="description" content={data && data.metaDescription} />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={(data && data?.metaTitle) || data.h1Title}
        />
        <meta
          property="og:description"
          content={data && data.metaDescription}
        />
        {data && data.featuredImage && data.featuredImage.url ? (
          <meta property="og:image" content={data && data.featuredImage.url} />
        ) : (
          <meta
            property="og:image"
            content={`${HOME_CANONICAL}/content/images/homepage/newSingleCareLogo.jpg`}
          />
        )}
        <meta
          property="og:url"
          content={`https://www.singlecare.com${location.pathname}`}
        />
        <meta property="og:site_name" content="SingleCare" />
        <meta property="aid_name" content={aidName} />
        <meta property="placement_name" content={`conditions-${slugType}`} />
        {isAdsVisible && (
          <script async rel="preload">
            {GPT_HEAD_CONDITIONS_ADD_ON_PAGE(slugType)}
          </script>
        )}
      </Helmet>
    );
  };

  const getLdJson = condition => {
    let structuredData = {
      '@context': 'https://schema.org',
      publisher: {
        '@type': 'Organization',
        name: 'SingleCare',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.singlecare.com/blog/wp-content/themes/singlecare_blog/images/top-bar-logo-new.png'
        }
      },
      '@type': 'NewsArticle',
      headline: data && data.h1Title,
      description: condition && condition.metaDescription,
      author: {
        '@type': 'Organization',
        name: 'SingleCare',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.singlecare.com/blog/wp-content/themes/singlecare_blog/images/top-bar-logo-new.png'
        }
      },
      url: `${CONDITIONS_DETAIL_CANONICAL}${condition && condition.slug}`,
      thumbnailUrl:
        condition && condition.featuredImage && condition.featuredImage.url,
      datePublished: condition && condition.publishedAt,
      dateModified: condition && condition.publishedAt,
      articleSection: `Conditions`,
      image:
        condition && condition.featuredImage && condition.featuredImage.url,
      keywords: [`${condition && condition.slugType}`],
      relatedLink: []
    };
    {
      data &&
        data.author &&
        (structuredData.creator = `${data && data.author}`);
    }
    return JSON.stringify(structuredData);
  };

  const displayAuthorProfile = (
    title = '',
    authorData = {},
    withDiscription = false
  ) => (
    <>
      {authorData?.name && (
        <AuthorProfile
          authorTitle={title}
          authorProfileLink={authorData?.reviewerPageOnSingleCare}
          authorDes={authorData?.title}
          authorName={authorData?.name}
          authorQualifications={authorData?.qualifications}
          authorImg={authorData?.photo}
          description={
            withDiscription
              ? documentToReactComponents(
                  JSON.parse(authorData?.biography || null)
                )
              : ''
          }
          className="userDataInfo"
        />
      )}
    </>
  );

  return (
    <div className="wrapperDetails">
      <div className="adsInCenter">
        <AdSlot
          id={typeAndAdsId[slugType]?.top}
          featureDisplayAdsFlag={isAdsVisible}
        />
      </div>
      {!_.isEmpty(data?.linkedConditionPages) &&
        data?.linkedConditionPages?.length > 0 && (
          <ConditionTabs
            breadcrumb={data?.breadcrumb}
            h2Title={data?.name}
            slug={data?.slug}
            slugType={data?.slugType}
            linkedConditionPages={data?.linkedConditionPages}
          />
        )}
      <div
        className={`mainContainer ${
          _.isEmpty(data?.linkedConditionPages) ? 'marginTop20' : ''
        }`}
      >
        <div
          className="leftAdsBannerContainer"
          data-ads-containter={
            VERTICAL_ADS_CONTAINER_ID.CONDITION_ADD_ON_PAGE_LEFT_SIDE
          }
        ></div>
        <div
          className="wrapperContiner"
          data-ads-containter={
            HORIZONTAL_ADS_CONTAINER_ID.CONDITIONS_ADD_ON_PAGE_IN_CONTENT
          }
        >
          {_.isEmpty(data?.linkedConditionPages) && (
            <Breadcrumb items={items}>
              {items.map(({ to, label }) => (
                <a key={label} href={to}>
                  {label}
                </a>
              ))}
              <span>{data.breadcrumb}</span>
            </Breadcrumb>
          )}
          {getMetaData()}
          <h1>{data && data.h1Title}</h1>
          <div
            className={`${
              !_.isEmpty(data?.tableOfContents) ? 'updateBottomMargin' : ''
            }`}
          >
            {data && (
              <div className="authorLists topLists">
                <>
                  {!_.isEmpty(data?.medicalReviewer) &&
                    displayAuthorProfile(
                      MEDICALLY_REVIEWED_BY,
                      data?.medicalReviewer,
                      false
                    )}
                  {!_.isEmpty(data?.author) &&
                    displayAuthorProfile(WRITTEN_BY, data?.author, false)}
                </>
              </div>
            )}
            <PublishedDate
              publishedAt={data?.publishedAt}
              updatedAt={data?.lastUpdatedDate}
            >
              {(data?.medicalReviewer || data?.author) && <FactCheckModal />}
            </PublishedDate>
          </div>
          {data && data.featuredImage && (
            <img
              className="featuredImage"
              src={data.featuredImage.url}
              alt={data.featuredImage.title}
            />
          )}
          <div className="mt-48">
            {!_.isEmpty(data?.tableOfContents) && (
              <TableOfContents content={data?.tableOfContents} />
            )}
          </div>
          <div className="mainWrapperData">
            {documentToReactComponents(
              introContent && introContent.json,
              renderOptions(introContent && introContent.links)
            )}
          </div>

          <KeyTakeawaysSection keyTakeaways={keyTakeaways} />

          <div className="mainWrapperData">
            {documentToReactComponents(
              content.json,
              renderOptions(content.links)
            )}
          </div>
          {!_.isEmpty(data && data.tableHeader) && (
            <>
              <h2 className="tableHead">{data && data.tableHeader} </h2>
              <table cellPadding="0" cellSpacing="0">
                <tr>
                  <th>Drug name</th>
                  <th>Learn more</th>
                  <th>See SingleCare price</th>
                </tr>
                {data &&
                  data.listOfDrugs.map((drug, index) => {
                    return (
                      <tr className="dataTable" key={index}>
                        <td>{drug.drugName}</td>
                        <td>
                          <a href={`/prescription/${drug.seoName}/what-is`}>
                            {' '}
                            {`${drug.seoName}`}
                            &nbsp;<span>details</span>
                          </a>
                        </td>
                        <td>
                          <div className="separatorLine mobile-only">
                            <img
                              src="../content/images/icons/lineBottom.png"
                              alt=""
                            />
                          </div>
                          <a
                            href={`/prescription/${drug.seoName}`}
                            className="sc-btn-pink rx-get-this-price themeFocus"
                          >
                            Get free coupon
                          </a>
                        </td>
                      </tr>
                    );
                  })}
              </table>
            </>
          )}
          {!_.isEmpty(summary) && (
            <div className="graySection">
              {documentToReactComponents(
                summary.json,
                renderOptions(summary.links)
              )}
            </div>
          )}
          {!_.isEmpty(faQs) && (
            <div className="mainWrapperData">
              {documentToReactComponents(faQs.json, renderOptions(faQs.links))}
            </div>
          )}
          <div className="mainWrapperData">
            {documentToReactComponents(
              references.json,
              renderOptions(references.links)
            )}
          </div>
          <section className="bottomUserDataInfo bottomInfo">
            {data && (
              <div className="authorLists">
                <>
                  {!_.isEmpty(data?.medicalReviewer) &&
                    displayAuthorProfile(
                      MEDICALLY_REVIEWED_BY,
                      data?.medicalReviewer,
                      true
                    )}
                  {!_.isEmpty(data?.author) &&
                    displayAuthorProfile(WRITTEN_BY, data?.author, true)}
                </>
              </div>
            )}
          </section>
          {!_.isEmpty(data && data.relatedConditionsContent) && (
            <section className="relatedDrugs mobile-section homepage-new">
              <section className="top-drugs">
                <article>
                  <h4>{`Related Conditions`}</h4>
                  <ul>
                    {data &&
                      data.relatedConditionsContent.map((item, id) => {
                        return (
                          <>
                            {item.conditionName && (
                              <li key={id}>
                                <a href={`/conditions/${item.slug}`}>
                                  {item.h1}
                                </a>
                              </li>
                            )}
                          </>
                        );
                      })}
                  </ul>
                </article>
              </section>
            </section>
          )}
          {!_.isEmpty(data && data.relatedDrugs) && (
            <section className="relatedDrugs mobile-section homepage-new">
              <section className="top-drugs">
                <article>
                  <h4>{`Related Drugs`}</h4>
                  <ul>
                    {data &&
                      data.relatedDrugs.map((item, id) => {
                        return (
                          <>
                            {item.drugName && (
                              <li key={id}>
                                <a href={`/prescription/${item.seoName}`}>
                                  {item.drugName}
                                </a>
                              </li>
                            )}
                          </>
                        );
                      })}
                  </ul>
                </article>
              </section>
            </section>
          )}
          {!_.isEmpty(relatedDrugInformation) && (
            <section className="relatedDrugs mobile-section homepage-new">
              <section className="top-drugs">
                <article>
                  <h4>{`Related Drug Information`}</h4>
                  <ul>
                    {relatedDrugInformation.map((item, id) => {
                      return (
                        <>
                          {item.name && item.type && (
                            <li key={id}>
                              <a
                                href={`/prescription/${item.slug}${
                                  RelatedDrugsInformationRoute[item.type]
                                }`}
                              >
                                {item.name}
                              </a>
                            </li>
                          )}
                        </>
                      );
                    })}
                  </ul>
                </article>
              </section>
            </section>
          )}
          {!_.isEmpty(blogData) && (
            <section className="homepage-new">
              <section id="news-research">
                <RelatedArticles
                  blogData={blogData}
                  drugName={data.name}
                  isRelatedArticle={false}
                />
              </section>
            </section>
          )}
          {!_.isEmpty(data && data.popularDrugs && data.popularDrugs.drugs) && (
            <PopularDrugs
              popularDrugs={data.popularDrugs.drugs}
              text="discounts"
            />
          )}
        </div>
        <div
          id="bottomStickyAd"
          className="bottomLastAds"
          data-ads-container={
            BOTTOM_ANCHOR_AD_CONTAINER_ID.CONDITIONS_ADD_ON_PAGE
          }
        >
          <AdSlot
            id={typeAndAdsId[slugType]?.bottomAnchor}
            enableCloseBtn={true}
            innerDivClassName="p-1"
            featureDisplayAdsFlag={isAdsVisible}
          />
        </div>

        <div
          className="rightAdsBannerContainer"
          data-ads-containter={
            VERTICAL_ADS_CONTAINER_ID.CONDITION_ADD_ON_PAGE_RIGHT_SIDE
          }
        ></div>
      </div>
      <div className="adsInCenter marginBottom-20">
        <AdSlot
          id={typeAndAdsId[slugType]?.bottom}
          featureDisplayAdsFlag={isAdsVisible}
        />
      </div>
    </div>
  );
};
export default ConditionAddOn;
